import { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  RiFlightTakeoffLine,
  RiFlightLandLine,
  RiArrowRightLine,
  RiTimeLine,
  RiTeamLine,
  RiMapPinLine,
  RiPlaneLine,
  RiBuilding2Line,
  RiArrowDownSLine,
  RiAddLine,
  RiCloseLine,
  RiArrowUpSLine,
  RiUserAddLine,
  RiEyeLine,
  RiEditLine,
  RiCheckboxCircleLine,
  RiSuitcase2Line,
  RiNurseLine,
  RiServiceBellLine
} from "@remixicon/react";
import { Skeleton } from "@/components/ui/Skeleton";
import { useAuthStore } from "@store/authStore";
import { normalizeRole, ROLES } from "@/lib/permissions";
import { apiClient } from "@/lib/api";
import { getAircraftModelName, getEffectiveAircraft } from "@/features/tails/utils";
import { selectAcceptedBidOption, selectPrimaryBidOption } from "@/features/quotes/utils";
import { useAirportsByIcao } from "@/features/quotes/services/airportService";
import AddServiceModal from "./AddServiceModal";
import AddPassengerDrawer from "./AddPassengerDrawer";
import AddCrewModal from "./AddCrewModal";
import TripSheetPreviewDrawer from "./TripSheetPreviewDrawer";
import FboInput from "./FboField";
import { getAirportTimezone } from '@/utils/airportTimezone';
import { formatUTCToDateTime } from '@/utils';
import { EmptyState2Gray } from "@assets/images";


const TimeInput = ({ value, onChange, className }) => {
  const [local, setLocal] = useState(value);
  useEffect(() => { setLocal(value); }, [value]);
  return (
    <input
      type="text"
      inputMode="numeric"
      placeholder="HH:MM"
      maxLength={5}
      value={local}
      onChange={e => {
        let v = e.target.value.replace(/[^0-9:]/g, '');
        if (v.length === 2 && !v.includes(':') && local.length < 3) v += ':';
        if (v.length > 5) v = v.slice(0, 5);
        setLocal(v);
      }}
      onBlur={() => {
        const match = local.match(/^(\d{1,2}):(\d{2})$/);
        if (match) {
          const h = Math.min(parseInt(match[1], 10), 23);
          const m = Math.min(parseInt(match[2], 10), 59);
          const formatted = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
          setLocal(formatted);
          onChange(formatted);
        } else {
          setLocal(value);
        }
      }}
      className={className}
    />
  );
};

const Section = ({ icon: Icon, title, children, isOpen, onToggle, completed, locked }) => {
  return (
    <div className={`bg-white rounded-2xl shadow-sm border ${locked ? "border-gray-100 opacity-60" : "border-gray-200"}`}>
      <button
        type="button"
        onClick={locked ? undefined : onToggle}
        className={`w-full flex items-center justify-between p-6 text-left transition-colors ${locked ? "cursor-not-allowed" : ""}`}
      >
        <div className="flex items-center gap-3 flex-1">
          {Icon && <Icon className={`w-5 h-5 flex-shrink-0 ${locked ? "text-gray-400" : "text-gray-700"}`} />}
          <h3 className={`text-base ${locked ? "text-gray-400" : "text-gray-900"}`}>{title}</h3>
        </div>
        {completed && (
          <span className="flex items-center justify-center w-6 h-6 rounded-full">
            <RiCheckboxCircleLine className="w-6 h-6 text-primary-500" />
          </span>
        )}
      </button>
      {isOpen && !locked && <div className="px-6 pb-6">{children}</div>}
    </div>
  );
};

const TripSheetPage = () => {
  const { userProfile } = useAuthStore();
  // Role gating for the "Request client passenger details" checkbox.
  // junior_broker can see it but can't toggle it; finance/compliance_qa
  // shouldn't see it at all.
  const role = normalizeRole(userProfile?.role);
  const isJuniorBroker = role === ROLES.JUNIOR_BROKER;
  // Finance + compliance_qa are view-only on the trip sheet: they shouldn't
  // see the "Request client passenger details" checkbox, the FBO selection
  // section, or have FBO be a hard requirement to navigate forward.
  const isTripSheetViewerRole = role === ROLES.FINANCE || role === ROLES.COMPLIANCE_QA;
  const [searchParams] = useSearchParams();
  const quoteId = searchParams.get("quoteid");
  const hasFlight = searchParams.get("flight");

  const { data: quoteData, isLoading: isQuoteLoading } = useQuery({
    queryKey: ["quoteDetails", quoteId],
    queryFn: () => apiClient.getQuoteById(quoteId),
    enabled: !!quoteId,
    staleTime: 0,
    refetchOnMount: "always",
    retry: 2,
    retryDelay: 1000,
    select: (data) => {
      // 1) Prefer the option explicitly marked ACCEPTED (backend should set this).
      let acceptedBid = null;
      let acceptedOption = null;
      for (const bidItem of data?.bid_items || []) {
        const bid = bidItem.bid;
        if (!bid?.bid_options) continue;
        const option = selectAcceptedBidOption(bid.bid_options);
        if (option) {
          acceptedBid = bid;
          acceptedOption = option;
          break;
        }
      }

      // 2) Fallback: when the parent quote is already past acceptance but no
      // option was tagged (backend currently leaves bid_options at DRAFT),
      // pick the shortlisted option — and as a last resort the first one.
      const POST_ACCEPT_STATUSES = [
        "ACCEPTED", "PASSENGERS_PENDING", "WAITING_FLIGHT",
        "IN_TRANSIT", "COMPLETED", "DIVERTED", "SIGNED",
      ];
      const quoteStatus = (data?.status || "").toUpperCase();
      if (!acceptedOption && POST_ACCEPT_STATUSES.includes(quoteStatus)) {
        for (const bidItem of data?.bid_items || []) {
          const bid = bidItem.bid;
          // Prefer shortlisted, ignore REJECTED / shortlisted:false rows —
          // avoids picking a soft-deleted tail as the trip-sheet subject
          // when the ACCEPTED status is missing on data drift.
          const option = selectPrimaryBidOption(bid?.bid_options);
          if (option) {
            acceptedBid = bid;
            acceptedOption = option;
            break;
          }
        }
      }

      return { ...data, acceptedBid, acceptedOption };
    },
  });

  const acceptedBid = quoteData?.acceptedBid ?? null;
  const acceptedOption = quoteData?.acceptedOption ?? null;

  const aircraftId = acceptedOption?.aircraft?.aircraft_id;
  const { data: aircraftDetails, isLoading: isAircraftLoading } = useQuery({
    queryKey: ["aircraftDetails", aircraftId],
    queryFn: () =>
      apiClient.getAircraftById(aircraftId, { include: "make_model, features" }),
    enabled: !!aircraftId,
    staleTime: 0,
    refetchOnMount: "always",
    retry: 2,
    retryDelay: 1000,
  });

  const { data: organization } = useQuery({
    queryKey: ["organization", userProfile?.org_id],
    queryFn: () => apiClient.getOrganizationById(userProfile.org_id),
    enabled: !!userProfile?.org_id,
  });

  const clientId = quoteData?.client?.client_id || quoteData?.client?.id;

  // Fetch existing quote passengers
  const { data: quotePassengers = [] } = useQuery({
    queryKey: ["quotePassengers", quoteId],
    queryFn: async () => {
      const res = await apiClient.getQuotePassengers(quoteId);
      return Array.isArray(res) ? res : (res?.data || []);
    },
    enabled: !!quoteId,
    staleTime: 0,
    refetchOnMount: "always",
  });

  // Fetch client passengers for enriched data (passport, visa, weight, etc.)
  const { data: clientPassengersList = [] } = useQuery({
    queryKey: ["clientPassengers", clientId],
    queryFn: async () => {
      const res = await apiClient.getClientPassengers(clientId);
      return Array.isArray(res) ? res : (res?.data || []);
    },
    enabled: !!clientId,
  });

  // Batch-fetch the specific airport codes on this trip's legs instead of
  // the old full-list fetch. React Query dedupes by ['airportByIcao', code]
  // so a downstream drawer asking for the same code shares the same
  // network call. `findAirport` becomes a synchronous Map lookup with the
  // same shape callers already expect.
  const flightIcaosForLookup = useMemo(() => {
    const codes = [];
    if (acceptedBid?.departure_airport) codes.push(acceptedBid.departure_airport);
    if (acceptedBid?.arrival_airport) codes.push(acceptedBid.arrival_airport);
    (acceptedBid?.leg_itinerary || []).forEach((l) => {
      if (l?.departure_airport) codes.push(l.departure_airport);
      if (l?.arrival_airport) codes.push(l.arrival_airport);
    });
    return codes;
  }, [acceptedBid]);
  const { byIcao: airportByIcao } = useAirportsByIcao(flightIcaosForLookup);

  const findAirport = useCallback(
    (icao) => {
      if (!icao) return null;
      return airportByIcao.get(String(icao).toUpperCase()) || null;
    },
    [airportByIcao]
  );

  // Build derived data
  const clientName = useMemo(() => {
    return [quoteData?.client?.first_name, quoteData?.client?.last_name]
      .filter(Boolean)
      .join(" ");
  }, [quoteData]);

  const clientCompany = quoteData?.client?.company_name || "";
  // aircraftDetails is re-fetched via getAircraftById (base record, no
  // quote context). Merge the per-quote override in here so the Build
  // Trip Sheet page's Trip Details section shows the broker's edited
  // tail / aircraft name (and the merged record flows into the preview
  // drawer + PDF too — TripSheetPreviewDrawer re-applies the merge
  // internally, which is idempotent for already-merged records).
  const mergedAircraft = getEffectiveAircraft(aircraftDetails, quoteData);
  const tailNumber = mergedAircraft?.tail_number || "";
  const aircraftName = mergedAircraft ? getAircraftModelName(mergedAircraft) : "";
  const passengerCount = acceptedBid?.passenger_count || 0;

  const departureAirport = useMemo(() => {
    const icao = acceptedBid?.departure_airport;
    const airport = findAirport(icao);
    return airport ? `${airport.name} (${icao})` : icao || "";
  }, [acceptedBid, findAirport]);

  const flights = useMemo(() => {
    if (!acceptedBid) return [];

    const buildFlight = (dep, arr, date) => ({
      pax: acceptedBid.passenger_count || 1,
      departure_date: date,
      departure_airport: dep || "",
      arrival_airport: arr || "",
    });

    if (acceptedBid.leg_itinerary?.length > 1) {
      return acceptedBid.leg_itinerary;
    } else if (acceptedBid.return_date) {
      const outbound =
        acceptedBid.leg_itinerary?.[0] ||
        buildFlight(
          acceptedBid.departure_airport,
          acceptedBid.arrival_airport,
          acceptedBid.departure_date
        );
      const returnLeg = buildFlight(
        outbound.arrival_airport || acceptedBid.arrival_airport,
        outbound.departure_airport || acceptedBid.departure_airport,
        acceptedBid.return_date
      );
      return [outbound, returnLeg];
    } else if (acceptedBid.leg_itinerary?.length === 1) {
      return acceptedBid.leg_itinerary;
    } else {
      return [
        buildFlight(
          acceptedBid.departure_airport,
          acceptedBid.arrival_airport,
          acceptedBid.departure_date
        ),
      ];
    }
  }, [acceptedBid]);

  // All editable state declarations
  const [crew, setCrew] = useState([]);
  const [passengers, setPassengers] = useState([]);
  const [services, setServices] = useState([]);
  // Per-leg FBO selections: fbos[legIdx] = { departure: ref|null, arrival: ref|null }
  const [fbos, setFbos] = useState([]);
  const [servicesInitialized, setServicesInitialized] = useState(false);
  const [filesSeeded, setFilesSeeded] = useState(false);
  const [passengersSeeded, setPassengersSeeded] = useState(false);
  const [passengerDrawerOpen, setPassengerDrawerOpen] = useState(false);
  const [editingPassengerIdx, setEditingPassengerIdx] = useState(null);
  const [requestClientPassengers, setRequestClientPassengers] = useState(true);
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [editingServiceIdx, setEditingServiceIdx] = useState(null);
  const [crewModalOpen, setCrewModalOpen] = useState(false);
  const [editingCrewIdx, setEditingCrewIdx] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [openSections, setOpenSections] = useState({ trip: true, crew: false, passengers: false, services: false });
  const [completedSections, setCompletedSections] = useState({ trip: false, crew: false, passengers: false, services: false });
  const [editableFlights, setEditableFlights] = useState([]);

  // Reset all editable state when quoteId changes (navigation or refresh)
  useEffect(() => {
    setCrew([]);
    setPassengers([]);
    setServices([]);
    setFbos([]);
    setServicesInitialized(false);
    setFilesSeeded(false);
    setPassengersSeeded(false);
    setOpenSections({ trip: true, crew: false, passengers: false, services: false });
    setCompletedSections({ trip: false, crew: false, passengers: false, services: false });
    setRequestClientPassengers(true);
    setEditableFlights([]);
    setPreviewOpen(false);
    setPassengerDrawerOpen(false);
    setEditingPassengerIdx(null);
    setCrewModalOpen(false);
    setEditingCrewIdx(null);
    setServiceModalOpen(false);
    setEditingServiceIdx(null);
  }, [quoteId]);

  useEffect(() => {
    if (flights.length > 0 && aircraftDetails && !servicesInitialized) {
      setServicesInitialized(true);
    }
  }, [flights, aircraftDetails, servicesInitialized]);

  // Sync editable flights from computed flights (only on initial load)
  useEffect(() => {
    if (flights.length > 0 && editableFlights.length === 0) {
      setEditableFlights(JSON.parse(JSON.stringify(flights)));
    }
  }, [flights, editableFlights.length]);

  const updateFlightAirport = useCallback((idx, field, value) => {
    setEditableFlights(prev => prev.map((f, i) => i === idx ? { ...f, [field]: value.toUpperCase() } : f));
    const side = field === 'departure_airport' ? 'departure' : 'arrival';
    setFbos(prev => {
      const next = [...prev];
      if (next[idx]) next[idx] = { ...next[idx], [side]: null };
      return next;
    });
  }, [setFbos]);

  const updateFlightDateTime = useCallback((idx, dateStr, timeStr) => {
    setEditableFlights(prev => {
      const flight = prev[idx];
      if (!flight) return prev;
      const moment = require('moment-timezone');
      const tz = getAirportTimezone(flight.departure_airport);
      const d = dateStr || formatUTCToDateTime(flight.departure_date, tz, 'YYYY-MM-DD');
      const t = timeStr || formatUTCToDateTime(flight.departure_date, tz, 'HH:mm');
      const parsed = moment.tz(`${d} ${t}`, 'YYYY-MM-DD HH:mm', tz);
      const iso = parsed.isValid() ? parsed.toISOString() : flight.departure_date;
      return prev.map((f, i) => i === idx ? { ...f, departure_date: iso } : f);
    });
  }, []);

  // Seed crew, additional services & request flag from previously-saved quote.files (once per quoteId)
  useEffect(() => {
    if (filesSeeded) return;
    const files = quoteData?.files;
    // Wait until the quote response actually carries a files object — a stale
    // cache hit can arrive without it and would otherwise lock us out.
    if (!files) return;
    const savedCrews = files.crews;
    const savedServices = files.additional_services;
    const savedRequestFlag = files.request_client_passengers;
    if (Array.isArray(savedCrews) && savedCrews.length > 0) {
      setCrew(savedCrews.map((c) => ({
        name: c.name || "",
        phone: c.phone || "",
        role: c.role || "",
      })));
    }
    if (Array.isArray(savedServices) && savedServices.length > 0) {
      setServices(savedServices.map((s) => ({
        name: s.name || "",
        details: s.details || "",
        fromQuote: true,
      })));
    }
    if (savedRequestFlag !== undefined && savedRequestFlag !== null) {
      setRequestClientPassengers(savedRequestFlag);
    }
    const savedFbo = files.fbo;
    if (Array.isArray(savedFbo) && savedFbo.length > 0) {
      setFbos(savedFbo.map((leg) => ({
        departure: leg?.departure || null,
        arrival: leg?.arrival || null,
      })));
    }
    setFilesSeeded(true);
  }, [quoteData, filesSeeded]);

  // Seed passengers from API quote passengers (once per quoteId)
  useEffect(() => {
    if (passengersSeeded || quotePassengers.length === 0) return;

    // If any quote passengers are linked to client passengers, wait for that
    // list to load before seeding — otherwise enrichment (passport_url, visa_url,
    // weight, additional_info) would be missed and never retried.
    const needsClientEnrichment = quotePassengers.some((qp) => qp.client_passenger_id);
    if (needsClientEnrichment && clientPassengersList.length === 0) return;

    setPassengersSeeded(true);

    const mapped = quotePassengers.map((qp) => {
      // Enrich with client passenger data if available
      const cp = clientPassengersList.find(
        (c) => c.client_passenger_id === qp.client_passenger_id
      );
      const info = cp?.additional_info || {};
      return {
        fullName: qp.passenger_name || cp?.full_name || "",
        dateOfBirth: cp?.date_of_birth || qp.birth_date || "",
        nationality: qp.nationality || cp?.nationality || "",
        passportFiles: [],
        visaFiles: [],
        existingPassportUrl: cp?.passport_url || "",
        existingVisaUrl: cp?.visa_url || "",
        clientPassengerId: qp.client_passenger_id || null,
        quotePassengerId: qp.quote_passenger_id || null,
        weight: cp?.weight || "",
        weightUnit: "lbs",
        dietaryCatering: info.dietary_catering || "",
        cabinEnvironment: info.cabin_environment || "",
        medicalMobility: info.medical_mobility || "",
        petAccommodations: info.pet_accommodations || "",
      };
    });

    setPassengers(mapped);
    if (mapped.length > 0) {
      // Preserve the "client completed" state (2); otherwise switch off the request mode
      setRequestClientPassengers((prev) => prev === 2 ? prev : false);
    }
  }, [quotePassengers, clientPassengersList, passengersSeeded]);

  // Update a single leg's departure/arrival FBO selection.
  const setLegFbo = useCallback((legIdx, side, ref) => {
    setFbos((prev) => {
      const next = [...prev];
      while (next.length <= legIdx) next.push({ departure: null, arrival: null });
      next[legIdx] = { ...next[legIdx], [side]: ref };
      return next;
    });
  }, []);

  // FBO is required to build the trip sheet: every leg needs both sides set.
  // Viewer roles (finance/compliance_qa) don't pick FBOs, so the gate passes
  // automatically for them.
  const allFbosSelected = useMemo(
    () => isTripSheetViewerRole || (flights.length > 0 && flights.every((_, idx) => fbos[idx]?.departure && fbos[idx]?.arrival)),
    [flights, fbos, isTripSheetViewerRole]
  );

  const handleAddCrewFromModal = (crewData) => {
    if (editingCrewIdx !== null) {
      updateCrew(editingCrewIdx, null, null, crewData);
      setEditingCrewIdx(null);
    } else {
      setCrew((prev) => [...prev, crewData]);
    }
    setCrewModalOpen(false);
  };

  const openEditCrew = (cIdx) => {
    setEditingCrewIdx(cIdx);
    setCrewModalOpen(true);
  };

  const removeCrew = (cIdx) => {
    setCrew((prev) => prev.filter((_, i) => i !== cIdx));
  };

  const updateCrew = (cIdx, field, value, fullData) => {
    setCrew((prev) => {
      const next = [...prev];
      next[cIdx] = fullData || { ...next[cIdx], [field]: value };
      return next;
    });
  };

  const addPassenger = (passengerData) => {
    setPassengers((prev) => {
      const isDuplicate = prev.some(
        (p) => p.fullName === passengerData.fullName && p.dateOfBirth === passengerData.dateOfBirth
      );
      if (isDuplicate) return prev;
      setRequestClientPassengers(false);
      return [...prev, passengerData];
    });
  };

  const updatePassenger = (pIdx, passengerData) => {
    setPassengers((prev) => {
      const next = [...prev];
      next[pIdx] = passengerData;
      return next;
    });
  };

  const removePassenger = async (pIdx) => {
    const passenger = passengers[pIdx];
    const qpId = passenger?.quotePassengerId;
    if (qpId) {
      try {
        await apiClient.deleteQuotePassenger(qpId);
      } catch (err) {
        console.error("Failed to delete quote passenger:", err);
      }
    }
    setPassengers((prev) => prev.filter((_, i) => i !== pIdx));
  };

  const handleAddPassengerFromDrawer = (passengerData) => {
    if (editingPassengerIdx !== null) {
      updatePassenger(editingPassengerIdx, passengerData);
      setEditingPassengerIdx(null);
    } else {
      addPassenger(passengerData);
    }
    setPassengerDrawerOpen(false);
  };

  const openEditPassenger = (pIdx) => {
    setEditingPassengerIdx(pIdx);
    setPassengerDrawerOpen(true);
  };

  const addService = (categoryName, details) => {
    if (editingServiceIdx !== null) {
      setServices((prev) => prev.map((s, i) =>
        i === editingServiceIdx ? { ...s, name: categoryName, details: details || "" } : s
      ));
      setEditingServiceIdx(null);
    } else {
      setServices((prev) => [...prev, { name: categoryName, details: details || "", fromQuote: false }]);
    }
    setServiceModalOpen(false);
  };

  const openEditService = (sIdx) => {
    setEditingServiceIdx(sIdx);
    setServiceModalOpen(true);
  };

  const removeService = (sIdx) => {
    setServices((prev) => prev.filter((_, i) => i !== sIdx));
  };

  const toggleSection = (key) => {
    setOpenSections((prev) => {
      const isCurrentlyOpen = prev[key];
      return { trip: false, crew: false, passengers: false, services: false, [key]: !isCurrentlyOpen };
    });
  };

  const goToSection = (completedKey, openKey, scrollId) => {
    setCompletedSections((prev) => ({ ...prev, [completedKey]: true }));
    setOpenSections({ trip: false, crew: false, passengers: false, services: false, [openKey]: true });
    setTimeout(() => {
      document.getElementById(scrollId)?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const isLoading = isQuoteLoading || isAircraftLoading;

  return (
    <div className="pb-32">
      <main className="px-1 py-3 w-full lg:max-w-[1000px] bg-white/50 backdrop-blur-sm rounded-[2rem] p-4 md:p-6">
        <div className="space-y-4">
          {isLoading ? (
            <div className="space-y-3">
              <div className="bg-white rounded-2xl p-5 space-y-4 shadow-md">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-4 w-36" />
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm">
                <Skeleton className="h-5 w-40" />
              </div>
              <div className="bg-white rounded-2xl p-5 shadow-sm">
                <Skeleton className="h-5 w-40" />
              </div>
            </div>
          ) : (
            <>
              {/* Trip Details */}
              <div id="trip-section">
              <Section icon={RiSuitcase2Line} title="Trip Details" isOpen={openSections.trip} onToggle={() => toggleSection("trip")} completed={completedSections.trip}>
                <div>
                  {/* Client */}
                  <div>
                    <p className="text-lg text-gray-900">{clientName || "—"}</p>
                    {clientCompany && (
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-0.5">
                        <RiBuilding2Line className="w-3.5 h-3.5" />
                        <span>{clientCompany}</span>
                      </div>
                    )}
                  </div>

                  {/* Tail Number */}
                  <p className="text-xl text-gray-900 tracking-tight mt-3 mb-1">{tailNumber || "—"}</p>

                  {/* Leg Itineraries */}
                  <div>
                    {editableFlights.map((flight, idx) => {
                      const tz = getAirportTimezone(flight.departure_airport);
                      const dateVal = flight.departure_date ? formatUTCToDateTime(flight.departure_date, tz, 'YYYY-MM-DD') : '';
                      const timeVal = flight.departure_date ? formatUTCToDateTime(flight.departure_date, tz, 'HH:mm') : '';
                      return (
                        <div
                          key={idx}
                          className="flex flex-wrap items-center gap-2 text-sm text-gray-700 border-y border-gray-200 py-2"
                        >
                          <span className="inline-flex items-center gap-1 bg-gray-200 rounded-full px-2 py-1 text-gray-800">
                            <RiFlightTakeoffLine className="w-4 h-4 shrink-0" />
                            <input
                              type="text"
                              value={flight.departure_airport}
                              onChange={e => updateFlightAirport(idx, 'departure_airport', e.target.value)}
                              className="bg-transparent outline-none w-14 font-mono text-sm uppercase"
                              maxLength={4}
                              placeholder="ICAO"
                            />
                          </span>
                          <RiArrowRightLine className="w-4 h-4 text-gray-500 shrink-0" />
                          <span className="inline-flex items-center gap-1 bg-gray-200 rounded-full px-2 py-1 text-gray-800">
                            <RiFlightLandLine className="w-4 h-4 shrink-0" />
                            <input
                              type="text"
                              value={flight.arrival_airport}
                              onChange={e => updateFlightAirport(idx, 'arrival_airport', e.target.value)}
                              className="bg-transparent outline-none w-14 font-mono text-sm uppercase"
                              maxLength={4}
                              placeholder="ICAO"
                            />
                          </span>
                          <div className="flex items-center gap-2 ml-auto">
                            <input
                              type="date"
                              value={dateVal}
                              onChange={e => {
                                if (!e.target.value) return;
                                updateFlightDateTime(idx, e.target.value, timeVal);
                              }}
                              className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            />
                            <div className="relative w-24">
                              <RiTimeLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-700 pointer-events-none" />
                              <TimeInput
                                value={timeVal}
                                onChange={t => updateFlightDateTime(idx, dateVal, t)}
                                className="w-full pl-9 pr-2 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Aircraft / Airport / Passengers */}
                  <div className="space-y-1.5 text-sm text-gray-500 my-3">
                    {aircraftName && (
                      <div className="flex items-center gap-2">
                        <RiPlaneLine className="w-3.5 h-3.5" />
                        <span>{aircraftName}</span>
                      </div>
                    )}
                    {(editableFlights[0]?.departure_airport || departureAirport) && (
                      <div className="flex items-center gap-2">
                        <RiMapPinLine className="w-3.5 h-3.5" />
                        <span>
                          {(() => {
                            const icao = editableFlights[0]?.departure_airport || acceptedBid?.departure_airport;
                            const airport = findAirport(icao);
                            return airport ? `${airport.name} (${icao})` : icao || '';
                          })()}
                        </span>
                      </div>
                    )}
                    {passengerCount > 0 && (
                      <div className="flex items-center gap-2">
                        <RiTeamLine className="w-3.5 h-3.5" />
                        <span>{passengerCount} passengers</span>
                      </div>
                    )}
                  </div>

                  {/* Aircraft Note — surfaces the broker's per-aircraft
                      note (custom_profile.extra_attributes.notes or
                      aircraft.extra_attributes.notes for owned aircraft).
                      Same amber-callout styling used on QuoteDetailsDrawer
                      and the itinerary email so all surfaces read as
                      the same note. */}
                  {mergedAircraft?.quote_notes && (
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-100 rounded-lg">
                      <h4 className="text-[10px] font-semibold text-amber-900 uppercase tracking-wider mb-1">Aircraft Note</h4>
                      <p className="text-xs text-amber-800 whitespace-pre-wrap">{mergedAircraft.quote_notes}</p>
                    </div>
                  )}

                  {/* FBO selection — Departure + Arrival side by side, per leg */}
                  {!isTripSheetViewerRole && flights.length > 0 && (
                    <div className="mt-4 mb-2">
                      <div className="space-y-4">
                        {(editableFlights.length ? editableFlights : flights).map((flight, idx) => (
                          <div key={idx}>
                            <div className="flex items-center gap-3 mb-1.5">
                              <div className="flex-1 h-px bg-gray-200" />
                              <span className="text-xs text-gray-500 whitespace-nowrap">
                                Leg {idx + 1}: {flight.departure_airport} → {flight.arrival_airport}
                              </span>
                              <div className="flex-1 h-px bg-gray-200" />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <FboInput
                                label="Departure Flight FBO *"
                                icon={RiFlightTakeoffLine}
                                icao={flight.departure_airport}
                                value={fbos[idx]?.departure || null}
                                onChange={(ref) => setLegFbo(idx, "departure", ref)}
                              />
                              <FboInput
                                label="Arrival Flight FBO *"
                                icon={RiFlightLandLine}
                                icao={flight.arrival_airport}
                                value={fbos[idx]?.arrival || null}
                                onChange={(ref) => setLegFbo(idx, "arrival", ref)}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Next Button */}
                  <button
                    onClick={() => {
                      if (!allFbosSelected) {
                        toast.error("Select a Departure and Arrival FBO for each leg first", { position: "bottom-right" });
                        return;
                      }
                      goToSection("trip", "passengers", "passenger-details");
                    }}
                    className="w-full flex items-center font-medium justify-center gap-1.5 py-3 border border-primary-50 text-primary-500 text-sm rounded-full transition-colors bg-primary-50"
                  >
                    Next
                    <RiArrowDownSLine className="w-4 h-4" />
                  </button>
                </div>
              </Section>
              </div>

            {/* Passenger Details */}
            <div id="passenger-details">
              <Section icon={RiTeamLine} title="Passenger Details" isOpen={openSections.passengers} onToggle={() => toggleSection("passengers")} completed={completedSections.passengers}>
                <div className="space-y-4">
                  {passengers.length === 0 ? (
                    <div className="flex flex-col items-center justify-center flex-1 min-h-[30vh]">
                      <img src={EmptyState2Gray} alt="No passengers" className="w-28 h-28 mb-3 opacity-50" />
                      <p className="text-sm text-gray-500">No passengers added yet</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      {passengers.map((pax, pIdx) => (
                        <div key={pIdx} className="flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2.5">
                          <span className="text-sm text-gray-900 truncate min-w-0">
                            {pax.fullName || `Passenger ${pIdx + 1}`}
                          </span>
                          <button
                            onClick={() => openEditPassenger(pIdx)}
                            className="text-gray-400 hover:text-gray-600 transition-colors ml-2 flex-shrink-0"
                          >
                            <RiEditLine className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {!isTripSheetViewerRole && (() => {
                    // Gate on the quote's booked passenger_count. Broker
                    // can't add more than they sold — button disables
                    // (with a hint) once the list matches the cap.
                    const atCapacity = passengerCount > 0 && passengers.length >= passengerCount;
                    return (
                      <>
                        <button
                          type="button"
                          onClick={() => { setEditingPassengerIdx(null); setPassengerDrawerOpen(true); }}
                          disabled={atCapacity}
                          className="w-full flex items-center font-medium justify-center gap-2 py-3 border border-primary-500 text-primary-500 text-sm rounded-full hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                        >
                          <RiUserAddLine className="w-4 h-4" />
                          Add Passenger
                        </button>
                        {atCapacity && (
                          <p className="text-xs text-red-500 text-center mt-1">
                            You've reached the maximum of {passengerCount} passenger{passengerCount === 1 ? '' : 's'} for this trip.
                          </p>
                        )}
                      </>
                    );
                  })()}
                  
                  {
                    requestClientPassengers === 2 ? (
                      <div className="flex items-center gap-2">
                        <RiCheckboxCircleLine className="w-5 h-5 text-green-500" />
                        <span className="text-sm text-gray-600">Client successfully added the passengers</span>
                      </div>
                    ) : isTripSheetViewerRole ? null : (
                      <label className={`flex items-center gap-2 ${isJuniorBroker ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          checked={isJuniorBroker ? false : !!requestClientPassengers}
                          disabled={isJuniorBroker}
                          onChange={(e) => setRequestClientPassengers(e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-primary-500 focus:ring-primary-400 disabled:cursor-not-allowed"
                        />
                        <span className="text-sm text-gray-600">Request client to complete passenger details</span>
                      </label>
                    )
                  }

                  <div className="flex items-center gap-3 mt-2">
                    <button
                      onClick={() => {
                        setOpenSections({ trip: true, crew: false, passengers: false, services: false });
                        setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 100);
                      }}
                      className="flex-1 flex items-center font-medium justify-center gap-1.5 py-3 bg-gray-50 border border-gray-200 text-gray-800 text-sm rounded-full hover:bg-gray-100 transition-colors"
                    >
                      <RiArrowUpSLine className="w-4 h-4" />
                      Back
                    </button>
                    {(() => {
                      // Passengers are required (broker must input them) when
                      // the "Request client to complete passenger details"
                      // checkbox is unchecked. If checked, the client will
                      // fill them in later and Next can proceed as a Skip.
                      const passengersRequired = !requestClientPassengers;
                      const nextDisabled = passengersRequired && passengers.length === 0;
                      return (
                        <button
                          onClick={() => {
                            if (nextDisabled) {
                              toast.error(
                                "Add at least one passenger, or check 'Request client to complete passenger details' to continue.",
                                { position: "bottom-right" }
                              );
                              return;
                            }
                            goToSection("passengers", "crew", "crew-details");
                          }}
                          disabled={nextDisabled}
                          title={nextDisabled ? "Add at least one passenger to continue" : undefined}
                          className={`flex-1 flex items-center font-medium justify-center gap-1.5 py-3 border text-sm rounded-full transition-colors ${
                            nextDisabled
                              ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                              : 'bg-primary-50 border-primary-50 text-primary-500 hover:bg-primary-100'
                          }`}
                        >
                          {requestClientPassengers && passengers.length === 0 ? 'Skip' : 'Next'}
                          <RiArrowDownSLine className="w-4 h-4" />
                        </button>
                      );
                    })()}
                  </div>
                </div>
              </Section>
            </div>

            {/* Crew Details */}
            <div id="crew-details">
              <Section icon={RiNurseLine} title="Crew Details" isOpen={openSections.crew} onToggle={() => toggleSection("crew")} completed={completedSections.crew}>
                <div className="space-y-4">
                  {crew.length === 0 ? (
                    <div className="flex flex-col items-center justify-center flex-1 min-h-[30vh]">
                      <img src={EmptyState2Gray} alt="No crews" className="w-28 h-28 mb-3 opacity-50" />
                      <p className="text-sm text-gray-500">No crews added yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {crew.map((c, cIdx) => (
                        <div key={cIdx} className="border border-gray-200 rounded-lg px-4 py-3 flex items-start justify-between">
                          <div className="flex-1 space-y-1">
                            <div className="flex items-center gap-1.5">
                              <span className="text-sm font-medium text-gray-900">
                                {c.role}{c.role && c.name ? ":" : ""} {c.name}
                              </span>
                            </div>
                            {c.phone && (
                              <p className="text-xs text-gray-400">{c.phone}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-2 ml-3">
                            <button
                              onClick={() => openEditCrew(cIdx)}
                              className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                              <RiEditLine className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => removeCrew(cIdx)}
                              className="text-gray-400 hover:text-red-500 transition-colors"
                            >
                              <RiCloseLine className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <button
                    onClick={() => { setEditingCrewIdx(null); setCrewModalOpen(true); }}
                    className="w-full flex items-center justify-center font-medium gap-2 py-3 border border-primary-500 text-primary-500 text-sm rounded-full hover:bg-gray-50 transition-colors"
                  >
                    <RiUserAddLine className="w-4 h-4" />
                    Add Crew Member
                  </button>

                  <div className="flex items-center gap-3 mt-2">
                    <button
                      onClick={() => {
                        setOpenSections({ trip: false, crew: false, passengers: true, services: false });
                        setTimeout(() => document.getElementById("passenger-details")?.scrollIntoView({ behavior: "smooth" }), 100);
                      }}
                      className="flex-1 flex items-center justify-center font-medium gap-1.5 py-3 bg-gray-50 border border-gray-200 text-gray-800 text-sm rounded-full hover:bg-gray-100 transition-colors"
                    >
                      <RiArrowUpSLine className="w-4 h-4" />
                      Back
                    </button>
                    <button
                      onClick={() => goToSection("crew", "services", "services-section")}
                      className="flex-1 flex items-center font-medium justify-center gap-1.5 py-3 border text-sm rounded-full transition-colors bg-primary-50 border-primary-50 text-primary-500"
                    >
                      Next
                      <RiArrowDownSLine className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </Section>
            </div>

            {/* Additional Services */}
            <div id="services-section">
            <Section icon={RiServiceBellLine} title="Additional Services" isOpen={openSections.services} onToggle={() => toggleSection("services")} completed={completedSections.services}>
              <div className="space-y-4">
                <div className="space-y-2">

                  {services.length === 0 ? (
                    <div className="flex flex-col items-center justify-center flex-1 min-h-[30vh]">
                      <img src={EmptyState2Gray} alt="No services" className="w-28 h-28 mb-3 opacity-50" />
                      <p className="text-sm text-gray-500">No services added yet</p>
                    </div>
                  ) : (
                    services.map((service, sIdx) => (
                    <div key={sIdx} className="flex items-center justify-between border border-gray-200 rounded-xl px-4 py-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-gray-900">{service.name}</p>
                        {service.details && (
                          <p className="text-xs text-gray-500 mt-0.5 truncate">{service.details}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-3">
                        <button
                          onClick={() => openEditService(sIdx)}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <RiEditLine className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => removeService(sIdx)}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <RiCloseLine className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                  )}
                  <button
                    onClick={() => setServiceModalOpen(true)}
                    className="w-full flex items-center justify-center gap-1.5 py-2.5 border border-primary-500 text-primary-500 font-medium text-sm rounded-full transition-colors"
                  >
                    <RiAddLine className="w-4 h-4" />
                    Add Service
                  </button>
                </div>
                <div className="flex items-center gap-3 mt-4">
                  <button
                    onClick={() => {
                      setOpenSections({ trip: false, crew: true, passengers: false, services: false });
                      setTimeout(() => document.getElementById("crew-details")?.scrollIntoView({ behavior: "smooth" }), 100);
                    }}
                    className="flex md:flex-1 items-center justify-center gap-1.5 py-3 px-6 bg-gray-50 border border-gray-200 text-gray-800 text-sm font-medium rounded-full hover:bg-gray-100 transition-colors"
                  >
                    <RiArrowUpSLine className="w-4 h-4" />
                    Back
                  </button>
                  <button
                    onClick={() => {
                      if (!allFbosSelected) {
                        toast.error("Select a Departure and Arrival FBO for each leg first", { position: "bottom-right" });
                        setOpenSections({ trip: true, crew: false, passengers: false, services: false });
                        setTimeout(() => document.getElementById("trip-section")?.scrollIntoView({ behavior: "smooth" }), 100);
                        return;
                      }
                      // Passenger details are required when the broker chose
                      // NOT to request them from the client. Block preview
                      // until at least one passenger is added or the checkbox
                      // is enabled.
                      if (!requestClientPassengers && passengers.length === 0) {
                        toast.error(
                          "Add at least one passenger, or check 'Request client to complete passenger details' to continue.",
                          { position: "bottom-right" }
                        );
                        setOpenSections({ trip: false, crew: false, passengers: true, services: false });
                        setTimeout(() => document.getElementById("passenger-details")?.scrollIntoView({ behavior: "smooth" }), 100);
                        return;
                      }
                      setCompletedSections((prev) => ({ ...prev, services: true }));
                      setPreviewOpen(true);
                    }}
                    className="flex-1 flex items-center justify-center gap-1.5 py-3 bg-primary-50 border border-primary-50 text-primary-500 font-medium text-xs sm:text-sm rounded-full transition-colors"
                  >
                    <RiEyeLine className="w-4 h-4 shrink-0" />
                    Preview / Edit Trip Sheet
                  </button>
                </div>
              </div>
            </Section>
            </div>
          </>
        )}
        </div>
      </main>
      {serviceModalOpen && (
        <AddServiceModal
          onSelect={addService}
          onClose={() => { setServiceModalOpen(false); setEditingServiceIdx(null); }}
          editingService={editingServiceIdx !== null ? services[editingServiceIdx] : null}
        />
      )}
      {crewModalOpen && (
        <AddCrewModal
          onClose={() => { setCrewModalOpen(false); setEditingCrewIdx(null); }}
          onAdd={handleAddCrewFromModal}
          editingCrew={editingCrewIdx !== null ? crew[editingCrewIdx] : null}
        />
      )}
      <AddPassengerDrawer
        isOpen={passengerDrawerOpen}
        onClose={() => { setPassengerDrawerOpen(false); setEditingPassengerIdx(null); }}
        onAdd={handleAddPassengerFromDrawer}
        onRemove={async () => { if (editingPassengerIdx !== null) { await removePassenger(editingPassengerIdx); setEditingPassengerIdx(null); setPassengerDrawerOpen(false); } }}
        editingPassenger={editingPassengerIdx !== null ? passengers[editingPassengerIdx] : null}
        clientId={quoteData?.client?.client_id || quoteData?.client?.id}
      />
      <TripSheetPreviewDrawer
        isOpen={previewOpen}
        onClose={() => setPreviewOpen(false)}
        quoteData={quoteData}
        acceptedBid={acceptedBid}
        aircraftDetails={mergedAircraft}
        organization={organization}
        flights={editableFlights.length ? editableFlights : flights}
        passengers={passengers}
        crew={crew}
        services={services}
        fbos={fbos}
        requestClientPassengers={
          // Roles that can't toggle the request shouldn't propagate a `true`
          // intent downstream. The `=== 2` server status (client already
          // completed) is preserved either way so historical state still shows.
          (isJuniorBroker || isTripSheetViewerRole) && requestClientPassengers !== 2
            ? false
            : requestClientPassengers
        }
        hasFlight={hasFlight}
      />
    </div>
  );
};

export default TripSheetPage;